import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { QuickFilterOptions } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { useBulkCollectionsUIShallowStore } from '@frontend/payments-state';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { BulkCollectionTabCallout } from '../../Collections/CollectionsList/callouts/bulk-collections-tab-callout';

export type StatusTabsProps = {
  selectedStatus: QuickFilterOptions;
  onChange: (selectedStatus: QuickFilterOptions) => void;
  showCollectionTab?: boolean;
  showCollectionTabCallout?: boolean;
  onDismissBanner?: () => void;
};

const styles = {
  statusTabs: css`
    margin-bottom: ${theme.spacing(3)};
  `,
  tab: css`
    font-size: ${theme.font.size.medium};
    padding: ${theme.spacing(1)} ${theme.spacing(2)};
  `,
};

const baseTrackingId = 'pay-portal-invoices--status-tabs--';

const tabs = (showCollectionTab?: boolean) => {
  const tabs = [];
  if (showCollectionTab) {
    tabs.push({ id: 'collections', label: 'Collections' });
  }
  tabs.push(
    ...[
      { id: 'allInvoices', label: 'All Requests' },
      { id: 'paid', label: 'Paid' },
      { id: 'unpaid', label: 'Unpaid' },
      { id: 'refunded', label: 'Refunds' },
      { id: 'failed', label: 'Failed' },
    ]
  );
  return tabs;
};

export const StatusTabs = ({
  selectedStatus,
  onChange,
  showCollectionTab,
  showCollectionTabCallout,
  onDismissBanner,
}: StatusTabsProps) => {
  const { t } = useTranslation('payments');
  const ref = useRef<HTMLDivElement>(null);
  const { shouldShowTabCallout, setShouldShowTabCallout, setShouldShowSelectAllCallout, dismissTabCallout } =
    useBulkCollectionsUIShallowStore(
      'shouldShowTabCallout',
      'setShouldShowTabCallout',
      'setShouldShowSelectAllCallout',
      'dismissTabCallout'
    );

  const handleOnChange = (id: string) => {
    if (id === 'collections') {
      setShouldShowSelectAllCallout(true);
    }
    onChange(id as QuickFilterOptions);
  };

  const handleCalloutDismiss = () => {
    dismissTabCallout();
    onDismissBanner?.();
  };

  useEffect(() => {
    // Sync tab callout state with prop
    setShouldShowTabCallout(!!showCollectionTabCallout);
  }, [showCollectionTabCallout]);

  return (
    <>
      <Tabs controlledTab={selectedStatus} onChange={handleOnChange}>
        <Tabs.Bar fullWidth={false} css={styles.statusTabs}>
          {tabs(showCollectionTab).map(({ id, label }) => (
            <Tabs.Tab key={id} trackingId={`${baseTrackingId}--${id}`} css={styles.tab} id={id}>
              <div ref={id === 'collections' ? ref : undefined}>{t(label)}</div>
            </Tabs.Tab>
          ))}
        </Tabs.Bar>
      </Tabs>
      {selectedStatus !== 'collections' && (
        <BulkCollectionTabCallout anchorRef={ref} showBanner={shouldShowTabCallout} onDismiss={handleCalloutDismiss} />
      )}
    </>
  );
};
